import { Fragment } from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./theme";
import type { AccentName } from "./theme";

export interface PipeNode {
  label: string;
  value: string;
  sub?: string;
  accent?: AccentName;
}

/**
 * The signature motif: a horizontal row of hard-bordered nodes whose connecting
 * edges DRAW ON left→right, then a particle dot travels each drawn edge to show
 * direction. Reveal is sequential, driven by the scene-local frame.
 */
export const PipelineGraph: React.FC<{
  nodes: PipeNode[];
  startFrame?: number;
  stepFrames?: number;
  nodeWidth?: number;
}> = ({ nodes, startFrame = 0, stepFrames = 18, nodeWidth = 150 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      {nodes.map((n, i) => {
        const c = COLORS[n.accent ?? "cyan"];
        const appear = spring({
          frame: frame - (startFrame + i * stepFrames),
          fps,
          config: SPRING.snappy,
          durationInFrames: 12,
        });

        const edgeStart = startFrame + i * stepFrames + 10;
        const grow = interpolate(
          frame,
          [edgeStart, edgeStart + stepFrames * 0.7],
          [0, 1],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
        );
        const loop = 40;
        const particleT =
          grow >= 1 ? ((frame - edgeStart) % loop) / loop : -1;
        const nextC = COLORS[nodes[i + 1]?.accent ?? "cyan"];

        return (
          <Fragment key={i}>
            <div
              style={{
                flex: `0 0 ${nodeWidth}px`,
                opacity: appear,
                transform: `translateY(${(1 - appear) * 14}px)`,
              }}
            >
              <div
                style={{
                  background: COLORS.panel,
                  border: `1.5px solid ${c}`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  boxShadow: `0 0 26px -14px ${c}`,
                }}
              >
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 13,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: COLORS.dim,
                  }}
                >
                  {n.label}
                </div>
                <div
                  style={{
                    fontFamily: FONTS.display,
                    fontWeight: 600,
                    fontSize: 24,
                    color: COLORS.text,
                    marginTop: 3,
                  }}
                >
                  {n.value}
                </div>
                {n.sub && (
                  <div
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 13,
                      color: c,
                      marginTop: 4,
                    }}
                  >
                    {n.sub}
                  </div>
                )}
              </div>
            </div>

            {i < nodes.length - 1 && (
              <div
                style={{
                  flex: "1 1 auto",
                  height: 2,
                  position: "relative",
                  margin: "0 -1px",
                  background: COLORS.line,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: 2,
                    width: `${grow * 100}%`,
                    background: `linear-gradient(90deg, ${c}, ${nextC})`,
                    boxShadow: `0 0 8px ${c}`,
                  }}
                />
                {particleT >= 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: -2.5,
                      left: `${particleT * 100}%`,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: c,
                      boxShadow: `0 0 10px ${c}`,
                    }}
                  />
                )}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
